import { Link, router, useForm, usePage } from "@inertiajs/react"
import { AlertCircle, Search } from "lucide-react"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import { useEffect, useRef, useState, type ReactNode } from "react"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { OsmCandidate, PoiMediaItem, PoiWarning } from "@/lib/types"
import type { SharedProps } from "@/types"
import { ImportSheet } from "./Import"

// ── Types ────────────────────────────────────────────────────────────────────

interface PoiFormData {
  name: string
  description: string
  address: string
  phone: string
  website_url: string
  booking_url: string
  google_maps_url: string
  lat: string
  lng: string
  category_ids: string[]
}

interface CategoryOption {
  id: number
  name: string
  icon?: string | null
}

interface Props {
  region: { id: number; name: string }
  poi: {
    id?: number
    name: string
    description: string
    address: string
    phone: string
    website_url: string
    booking_url: string
    google_maps_url: string
    lat: number | null
    lng: number | null
    category_ids: number[]
  }
  categories: CategoryOption[]
  media: PoiMediaItem[]
  urls: {
    submit: string
    method: "post" | "patch"
    region: string
    index: string
    media_create: string | null
    import_search_url?: string
  }
  warnings?: PoiWarning[]
}

// ── Map style (OpenStreetMap raster) ─────────────────────────────────────────

const OSM_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: "© OpenStreetMap contributors",
    },
  },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
}

const DEFAULT_CENTER: [number, number] = [-9.1393, 38.7223] // Lisbon

// ── MapLocationPicker ─────────────────────────────────────────────────────────

interface MapLocationPickerProps {
  lat: string
  lng: string
  onPick: (lat: string, lng: string) => void
}

function MapLocationPicker({ lat, lng, onPick }: MapLocationPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markerRef = useRef<maplibregl.Marker | null>(null)
  const [mapError, setMapError] = useState(false)

  // Keep a stable ref so the click handler always sees the latest callback
  const onPickRef = useRef(onPick)
  onPickRef.current = onPick

  useEffect(() => {
    if (!containerRef.current) return

    const hasCoords = lat !== "" && lng !== ""
    const initialCenter: [number, number] = hasCoords
      ? [parseFloat(lng), parseFloat(lat)]
      : DEFAULT_CENTER

    let map: maplibregl.Map
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: OSM_STYLE,
        center: initialCenter,
        zoom: hasCoords ? 14 : 5,
      })
    } catch {
      setMapError(true)
      return
    }

    map.addControl(new maplibregl.NavigationControl(), "top-right")
    mapRef.current = map

    map.on("load", () => {
      // Place initial marker if coordinates exist
      if (hasCoords) {
        markerRef.current = new maplibregl.Marker({ color: "#C2553A" })
          .setLngLat([parseFloat(lng), parseFloat(lat)])
          .addTo(map)
      }
    })

    // Click handler: drop / move the marker and update form data
    map.on("click", (e) => {
      const newLng = e.lngLat.lng.toFixed(6)
      const newLat = e.lngLat.lat.toFixed(6)

      if (markerRef.current) {
        markerRef.current.setLngLat([e.lngLat.lng, e.lngLat.lat])
      } else {
        markerRef.current = new maplibregl.Marker({ color: "#C2553A" })
          .setLngLat([e.lngLat.lng, e.lngLat.lat])
          .addTo(map)
      }

      onPickRef.current(newLat, newLng)
    })

    return () => {
      markerRef.current?.remove()
      markerRef.current = null
      map.remove()
      mapRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (mapError) {
    return (
      <p className="text-sm text-muted-foreground p-4 border rounded">
        Map unavailable — enter coordinates manually below.
      </p>
    )
  }

  return (
    <div ref={containerRef} className="h-72 w-full overflow-hidden rounded-lg border" />
  )
}

// ── RegionPoiUploadForm ───────────────────────────────────────────────────────

function RegionPoiUploadForm({
  uploadUrl,
}: {
  uploadUrl: string
}) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, post, processing, reset } = useForm<{
    file: File | null
    alt_text: string
  }>({ file: null, alt_text: "" })

  function handleUpload(e: React.FormEvent) {
    e.preventDefault()
    post(uploadUrl, { forceFormData: true, onSuccess: () => reset() })
  }

  return (
    <form onSubmit={handleUpload} className="space-y-2">
      <div>
        <Input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="cursor-pointer"
          onChange={(e) => setData("file", e.target.files?.[0] ?? null)}
        />
        <p className="mt-1 text-xs text-muted-foreground">JPEG, PNG, or WebP — max 10 MB</p>
      </div>
      <Input
        placeholder="Alt text (optional)"
        value={data.alt_text}
        onChange={(e) => setData("alt_text", e.target.value)}
      />
      {errors.file && <p className="text-xs text-destructive">{errors.file}</p>}
      <Button type="submit" size="sm" disabled={!data.file || processing}>
        {processing ? "Uploading…" : "Upload photo"}
      </Button>
    </form>
  )
}

// ── Main Form Page ────────────────────────────────────────────────────────────

export default function RegionPoiForm({ region, poi, categories, media, urls, warnings }: Props) {
  const { errors } = usePage<SharedProps>().props
  const isEdit = poi.id !== undefined

  const [coverMediaId, setCoverMediaId] = useState<number | null>(
    media.length > 0 ? media[0].id : null,
  )
  const [warningsDismissed, setWarningsDismissed] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [osmMeta, setOsmMeta] = useState<{ osm_id: string; osm_type: string } | null>(null)

  useEffect(() => {
    if (coverMediaId === null && media.length > 0) setCoverMediaId(media[0].id)
  }, [media, coverMediaId])

  const { data, setData, post, patch, processing } = useForm<PoiFormData>({
    name:            poi.name,
    description:     poi.description,
    address:         poi.address,
    phone:           poi.phone,
    website_url:     poi.website_url,
    booking_url:     poi.booking_url,
    google_maps_url: poi.google_maps_url,
    lat:             poi.lat != null ? String(poi.lat) : "",
    lng:             poi.lng != null ? String(poi.lng) : "",
    category_ids:    poi.category_ids.map(String),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (urls.method === "patch") {
      patch(urls.submit)
    } else {
      post(urls.submit)
    }
  }

  function toggleCategory(id: string) {
    const next = data.category_ids.includes(id)
      ? data.category_ids.filter((cid) => cid !== id)
      : [...data.category_ids, id]
    setData("category_ids", next)
  }

  function handleSetCover(m: PoiMediaItem) {
    if (m.id === coverMediaId) return
    const snapshot = coverMediaId
    setCoverMediaId(m.id)
    router.patch(
      m.set_cover_url,
      {},
      { preserveScroll: true, onError: () => setCoverMediaId(snapshot) },
    )
  }

  function handleImportSelect(candidate: OsmCandidate) {
    setData((prev) => ({
      ...prev,
      name:    candidate.name,
      address: candidate.address,
      lat:     String(candidate.lat),
      lng:     String(candidate.lng),
    }))
    setOsmMeta({ osm_id: candidate.osm_id, osm_type: candidate.osm_type })
    setWarningsDismissed(false)
  }

  const pageTitle = isEdit ? `Edit POI — ${region.name}` : `Add POI — ${region.name}`

  return (
    <div className="mx-auto max-w-2xl pb-16">
      {/* Breadcrumb + header */}
      <div className="mb-6">
        <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
          <Link href={urls.region} className="hover:underline">
            {region.name}
          </Link>
          <span aria-hidden="true">/</span>
          <Link href={urls.index} className="hover:underline">
            POIs
          </Link>
          <span aria-hidden="true">/</span>
          <span>{isEdit ? "Edit" : "New"}</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">{pageTitle}</h1>
      </div>

      {/* Photos section — only shown when editing an existing POI */}
      {isEdit && urls.media_create && (
        <section className="mb-6 rounded-lg border p-5">
          <h2 className="mb-4 text-sm font-semibold">Photos</h2>
          {media.length > 0 ? (
            <div className="mb-4 grid grid-cols-3 gap-3">
              {media.map((m) => {
                const isCover = m.id === coverMediaId
                return (
                  <div key={m.id} className="group relative aspect-square">
                    <img
                      src={m.url ?? ""}
                      alt={m.alt_text ?? ""}
                      className="h-full w-full rounded-md object-cover"
                    />
                    {isCover && (
                      <span className="absolute bottom-1 left-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                        Cover
                      </span>
                    )}
                    {!isCover && (
                      <button
                        type="button"
                        className="absolute bottom-1 left-1 rounded bg-black/50 px-1.5 py-0.5 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={() => handleSetCover(m)}
                      >
                        Set as cover
                      </button>
                    )}
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute right-1 top-1 size-7 opacity-0 transition-opacity group-hover:opacity-100"
                      onClick={() => {
                        if (!window.confirm("Delete this photo?")) return
                        router.delete(m.destroy_url)
                      }}
                      aria-label="Delete photo"
                    >
                      ×
                    </Button>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="mb-4 text-sm text-muted-foreground">No photos yet.</p>
          )}
          <RegionPoiUploadForm
            uploadUrl={urls.media_create}
          />
        </section>
      )}

      {/* POI details form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Name */}
        <div className="grid gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="poi-name">
              Name <span className="text-destructive">*</span>
            </Label>
            {urls.import_search_url && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs text-muted-foreground"
                onClick={() => setImportOpen(true)}
              >
                <Search className="h-3.5 w-3.5" />
                Import from OSM
              </Button>
            )}
          </div>
          <Input
            id="poi-name"
            value={data.name}
            onChange={(e) => {
              setData("name", e.target.value)
              setWarningsDismissed(false)
            }}
            required
            autoFocus
          />
          {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
          {!warningsDismissed && warnings && warnings.length > 0 && (
            <Alert className="border-yellow-300 bg-yellow-50">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                Similar POI already exists: <strong>{warnings[0].name}</strong>.{" "}
                You can still save — this is just a warning.
                <button
                  type="button"
                  onClick={() => setWarningsDismissed(true)}
                  className="ml-2 underline text-yellow-700 hover:text-yellow-900"
                >
                  Dismiss
                </button>
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Description */}
        <div className="grid gap-1.5">
          <Label htmlFor="poi-description">Description</Label>
          <Textarea
            id="poi-description"
            value={data.description}
            onChange={(e) => setData("description", e.target.value)}
            rows={3}
            placeholder="A brief description for guests…"
          />
          {errors.description && (
            <p className="text-xs text-destructive">{errors.description}</p>
          )}
        </div>

        {/* Categories */}
        <div className="grid gap-2">
          <Label>
            Categories <span className="text-destructive">*</span>
          </Label>
          {categories.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => {
                const selected = data.category_ids.includes(String(cat.id))
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggleCategory(String(cat.id))}
                    className="focus:outline-none"
                  >
                    <Badge
                      variant={selected ? "default" : "outline"}
                      className="cursor-pointer select-none"
                    >
                      {cat.icon && <span className="mr-1">{cat.icon}</span>}
                      {cat.name}
                    </Badge>
                  </button>
                )
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No categories configured yet.</p>
          )}
          {errors.category_ids && (
            <p className="text-xs text-destructive">{errors.category_ids}</p>
          )}
        </div>

        {/* Location picker */}
        <div className="grid gap-2">
          <Label>
            Location <span className="text-destructive">*</span>
          </Label>
          <p className="text-xs text-muted-foreground">
            Click on the map to pin the exact location.
          </p>
          <MapLocationPicker
            lat={data.lat}
            lng={data.lng}
            onPick={(newLat, newLng) => {
              setData((prev) => ({ ...prev, lat: newLat, lng: newLng }))
            }}
          />
          {/* Coordinate fallback inputs */}
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1">
              <Label htmlFor="poi-lat" className="text-xs text-muted-foreground">
                Latitude
              </Label>
              <Input
                id="poi-lat"
                type="number"
                step="any"
                placeholder="38.7223"
                value={data.lat}
                onChange={(e) => setData("lat", e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="poi-lng" className="text-xs text-muted-foreground">
                Longitude
              </Label>
              <Input
                id="poi-lng"
                type="number"
                step="any"
                placeholder="-9.1393"
                value={data.lng}
                onChange={(e) => setData("lng", e.target.value)}
                className="h-8 text-xs"
              />
            </div>
          </div>
          {data.lat && data.lng && (
            <p className="text-xs text-muted-foreground">
              Pinned at {parseFloat(data.lat).toFixed(5)}, {parseFloat(data.lng).toFixed(5)}
            </p>
          )}
          {errors.location && <p className="text-xs text-destructive">{errors.location}</p>}
          {errors.lat && <p className="text-xs text-destructive">{errors.lat}</p>}
          {errors.lng && <p className="text-xs text-destructive">{errors.lng}</p>}
        </div>

        {/* Address */}
        <div className="grid gap-1.5">
          <Label htmlFor="poi-address">Address</Label>
          <Input
            id="poi-address"
            value={data.address}
            onChange={(e) => setData("address", e.target.value)}
            placeholder="Street address or landmark"
          />
          {errors.address && <p className="text-xs text-destructive">{errors.address}</p>}
        </div>

        {/* Phone + Website */}
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="poi-phone">Phone</Label>
            <Input
              id="poi-phone"
              type="tel"
              value={data.phone}
              onChange={(e) => setData("phone", e.target.value)}
            />
            {errors.phone && <p className="text-xs text-destructive">{errors.phone}</p>}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="poi-website">Website URL</Label>
            <Input
              id="poi-website"
              type="url"
              value={data.website_url}
              onChange={(e) => setData("website_url", e.target.value)}
              placeholder="https://…"
            />
            {errors.website_url && (
              <p className="text-xs text-destructive">{errors.website_url}</p>
            )}
          </div>
        </div>

        {/* Booking URL */}
        <div className="grid gap-1.5">
          <Label htmlFor="poi-booking">Booking URL</Label>
          <Input
            id="poi-booking"
            type="url"
            value={data.booking_url}
            onChange={(e) => setData("booking_url", e.target.value)}
            placeholder="https://…"
          />
          {errors.booking_url && (
            <p className="text-xs text-destructive">{errors.booking_url}</p>
          )}
        </div>

        {/* Google Maps URL */}
        <div className="grid gap-1.5">
          <Label htmlFor="poi-maps">Google Maps URL</Label>
          <Input
            id="poi-maps"
            type="url"
            value={data.google_maps_url}
            onChange={(e) => setData("google_maps_url", e.target.value)}
            placeholder="https://maps.google.com/…"
          />
          {errors.google_maps_url && (
            <p className="text-xs text-destructive">{errors.google_maps_url}</p>
          )}
        </div>

        {/* Hidden force_save flag — set to true when warnings have been dismissed */}
        <input type="hidden" name="force_save" value={warningsDismissed ? "true" : "false"} />

        {/* Hidden OSM metadata — populated when a candidate is selected from ImportSheet */}
        {osmMeta && (
          <>
            <input type="hidden" name="poi[metadata][osm_id]" value={osmMeta.osm_id} />
            <input type="hidden" name="poi[metadata][osm_type]" value={osmMeta.osm_type} />
          </>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 border-t pt-4">
          <Button type="button" variant="outline" asChild>
            <Link href={urls.index}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? "Saving…" : isEdit ? "Save changes" : "Create POI"}
          </Button>
        </div>
      </form>
      {urls.import_search_url && (
        <ImportSheet
          open={importOpen}
          onOpenChange={setImportOpen}
          searchUrl={urls.import_search_url}
          onSelect={handleImportSelect}
        />
      )}
    </div>
  )
}

RegionPoiForm.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
